import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { CalendarOff, Trash2, CalendarDays } from 'lucide-react';
import PageHeader from '../ui-pro/PageHeader';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LEAVE_TYPES = {
  yillik: { label: 'Yıllık İzin', cls: 'bg-blue-100 text-blue-700' },
  raporlu: { label: 'Raporlu', cls: 'bg-rose-100 text-rose-700' },
  ucretsiz: { label: 'Ücretsiz', cls: 'bg-slate-100 text-slate-700' },
  haftalik: { label: 'Haftalık İzin', cls: 'bg-emerald-100 text-emerald-700' },
  diger: { label: 'Diğer', cls: 'bg-amber-100 text-amber-700' },
};
const fmt = (d) => { try { return new Date(d).toLocaleDateString('tr-TR'); } catch { return d; } };

const LeavesTab = ({ personnelList = [] }) => {
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pid, setPid] = useState('all');
  const nameMap = useMemo(() => Object.fromEntries(personnelList.map(p => [p.id, p.name])), [personnelList]);

  const fetchLeaves = async () => {
    setLoading(true);
    try { const res = await axios.get(`${API}/personnel-leaves`); setLeaves(res.data || []); }
    catch { toast.error('İzinler yüklenemedi'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchLeaves(); }, []);

  const del = async (id) => {
    if (!window.confirm('Bu izin kaydı silinsin mi?')) return;
    try { await axios.delete(`${API}/personnel-leaves/${id}`); toast.success('İzin silindi'); fetchLeaves(); }
    catch { toast.error('Silinemedi'); }
  };

  const filtered = pid === 'all' ? leaves : leaves.filter(l => l.personnel_id === pid);
  const today = new Date().toISOString().split('T')[0];
  const onLeaveToday = leaves.filter(l => l.start_date <= today && l.end_date >= today);

  return (
    <div className="space-y-4" data-testid="leaves-tab-root">
      <PageHeader
        icon={CalendarOff}
        title="İzin Yönetimi"
        description={`Tüm personel izinleri — bugün ${onLeaveToday.length} personel izinli`}
        testid="leaves-page-header"
      >
        <div className="flex items-center gap-2">
          <span className="text-[12px] text-slate-500">Personel:</span>
          <Select value={pid} onValueChange={setPid}>
            <SelectTrigger className="w-[220px] h-9" data-testid="leaves-personnel-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Personel</SelectItem>
              {personnelList.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="ml-auto text-[12px] text-slate-500"><span className="font-semibold text-slate-800">{filtered.length}</span> kayıt</span>
        </div>
      </PageHeader>

      <Card><CardContent className="p-0">
        {loading ? <p className="text-center py-10 text-slate-400 text-sm">Yükleniyor...</p>
          : filtered.length === 0 ? <p className="text-center py-10 text-slate-400 text-sm flex items-center justify-center gap-2"><CalendarDays className="w-4 h-4" /> İzin kaydı yok</p>
          : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="leaves-table">
                <thead className="bg-slate-50 border-b"><tr className="text-left text-xs text-slate-600">
                  <th className="px-3 py-2 font-medium">Personel</th>
                  <th className="px-3 py-2 font-medium">Tür</th>
                  <th className="px-3 py-2 font-medium">Başlangıç</th>
                  <th className="px-3 py-2 font-medium">Bitiş</th>
                  <th className="px-2 py-2 font-medium text-center">Gün</th>
                  <th className="px-3 py-2 font-medium">Not</th>
                  <th className="px-2 py-2"></th>
                </tr></thead>
                <tbody>
                  {filtered.map(l => {
                    const t = LEAVE_TYPES[l.leave_type] || LEAVE_TYPES.diger;
                    return (
                      <tr key={l.id} className="border-b hover:bg-slate-50" data-testid={`leave-row-${l.id}`}>
                        <td className="px-3 py-2 font-medium text-slate-800">{nameMap[l.personnel_id] || 'Personel'}</td>
                        <td className="px-3 py-2"><Badge className={`${t.cls} border-0 text-[10px]`}>{t.label}</Badge></td>
                        <td className="px-3 py-2 text-slate-600">{fmt(l.start_date)}</td>
                        <td className="px-3 py-2 text-slate-600">{fmt(l.end_date)}</td>
                        <td className="px-2 py-2 text-center font-semibold">{l.days}</td>
                        <td className="px-3 py-2 text-slate-500 text-xs max-w-[220px] truncate">{l.notes || '-'}</td>
                        <td className="px-2 py-2 text-right"><Button size="sm" variant="ghost" onClick={() => del(l.id)} className="h-7 w-7 p-0" data-testid={`leave-delete-${l.id}`}><Trash2 className="w-3.5 h-3.5 text-rose-500" /></Button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
      </CardContent></Card>
      <p className="text-[11px] text-slate-400 px-1">İzin eklemek için Personel sayfasında ilgili personelin detayını açıp "İzin" bölümünü kullanın. İzinli günlerde rutinler otomatik kapanır.</p>
    </div>
  );
};

export default LeavesTab;
